import { CSSProperties, forwardRef, HTMLAttributes, memo } from "react";
import classnames from "classnames";

import styles from "./style.module.scss";

export type CircularProgressSize = "xxs" | "xs" | "sm" | "md" | "lg"

export type CircularProgressProps = HTMLAttributes<HTMLDivElement> & {
  value: number;
  label?: string;
  size?: CircularProgressSize;
  shape?: "circle" | "halfCircle";
}

export const CircularProgress = memo(forwardRef<HTMLDivElement, CircularProgressProps>((
  {
    value,
    label,
    size = "xxs",
    shape = "circle",
    className,
    style,
    ...props
  },
  ref,
) => {
  const sizeValue = {
    xxs: 64,
    xs: 160,
    sm: 200,
    md: 240,
    lg: 280,
  };
  const radius = (sizeValue[size] - (size === "xxs" ? 10 : 30)) / 2;
  const dashArray = radius * Math.PI * 2;
  const dashOffset = dashArray - (dashArray * value);
  const halfSize = sizeValue[size] / 2;

  return (
    <div
      className={classnames(styles.root, styles[size], styles[shape], className, { [styles.withLabel]: !!label })}
      ref={ref}
      style={{
        "--circular-progress-size": `${sizeValue[size]}`,
        "--circular-progress-radius": `${radius}px`,
        ...style,
      } as CSSProperties}
      {...props}
      data-circular-size={size}
      data-circular-shape={shape}
      data-circular-value={value}
    >
      <div className={styles.textContainer}>
        {label && <p className={styles.label}>{label}</p>}
        <p className={styles.value}>
          {`${(value * 100).toFixed(0)}%`}
        </p>
      </div>
      <svg
        className={styles.svgContainer}
        viewBox={`0 0 ${sizeValue[size]} ${sizeValue[size]}`}
      >
        <circle
          className={classnames(styles.svgBackground, styles.svgStroke)}
          transform={`rotate(-${shape === "halfCircle" ? "180" : "90"} ${halfSize} ${halfSize})`}
          style={{
            strokeDasharray: shape === "halfCircle" ? `${radius * Math.PI * 2} ${radius * Math.PI}` : "none",
            strokeDashoffset: shape === "halfCircle" ? `${radius * Math.PI}` : "none",
          }}
        />
        <circle
          className={classnames(styles.svgProgress, styles.svgStroke)}
          transform={`rotate(-${shape === "halfCircle" ? "180" : "90"} ${halfSize} ${halfSize})`}
          style={{
            strokeDasharray: dashArray,
            strokeDashoffset:
              shape === "halfCircle"
                ? dashArray - (dashArray * (value / 2))
                : dashOffset,
          }}
        />
      </svg>
    </div>
  );
}));